import {
  Stack,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip
} from '@mui/material';
import { useIntl } from 'react-intl';
import InfoIcon from '@mui/icons-material/Info';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import PropTypes from 'prop-types';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import InfoRow from 'pages/importExportConfiguration/components/importConfiguration/InfoRow';

const IMPORT_MODES = {
  REPLACE: 'REPLACE',
  DUPLICATE: 'DUPLICATE'
};

export default function ImportConfigurationMetadata({
  metadata,
  importMode,
  onImportModeChange,
  onCancel,
  onImport,
  isImporting = false,
  onExpandAll,
  onCollapseAll,
  onSelectAll,
  onDeselectAll
}) {
  const { formatMessage: msg } = useIntl();

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  return (
    <Stack display="flex" direction="column" sx={{ width: '100%', p: 2, pt: 0, borderRadius: 1 }}>
      <Stack display="flex" direction="row" alignItems="center" justifyContent="space-between">
        <Stack display="flex" direction="row" alignItems="center">
          <InfoIcon color="text" fontSize="medium" sx={{ mr: '0.75rem' }} />
          <MDTypography variant="h5">
            {msg({ id: 'page.importConfiguration.metadata.title' })}
          </MDTypography>
        </Stack>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={msg({ id: 'tooltip.expandAll' })}>
            <IconButton onClick={onExpandAll} color="primary">
              <UnfoldMoreIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={msg({ id: 'tooltip.collapseAll' })}>
            <IconButton onClick={onCollapseAll} color="primary">
              <UnfoldLessIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={msg({ id: 'tooltip.selectAll' })}>
            <IconButton onClick={onSelectAll} color="primary">
              <CheckBoxIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={msg({ id: 'tooltip.deselectAll' })}>
            <IconButton onClick={onDeselectAll} color="primary">
              <CheckBoxOutlineBlankIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      <Divider sx={{ mb: 2, mt: 0.5 }} />

      <FormControl variant="filled" fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel id="import-mode-select-label">
          {msg({ id: 'page.importConfiguration.importMode.label' })}
        </InputLabel>
        <Select
          labelId="import-mode-select-label"
          value={importMode}
          onChange={(e) => onImportModeChange(e.target.value)}
          variant="filled"
        >
          <MenuItem value={IMPORT_MODES.REPLACE}>
            {msg({ id: 'page.importConfiguration.importMode.replace' })}
          </MenuItem>
          {/*<MenuItem value={IMPORT_MODES.DUPLICATE}>*/}
          {/*  {msg({ id: 'page.importConfiguration.importMode.duplicate' })}*/}
          {/*</MenuItem>*/}
        </Select>
      </FormControl>

      <Stack spacing={1.5} flex={1} overflow="auto" sx={{ scrollbarWidth: 'thin' }}>
        <InfoRow label={msg({ id: 'field.uuid.label' })} value={metadata.uuid} />

        <InfoRow
          label={msg({ id: 'field.exportDate.label' })}
          value={formatDate(metadata.exportDate)}
        />

        <InfoRow label={msg({ id: 'field.exportedBy.label' })} value={metadata.exportedBy} />

        <InfoRow
          label={msg({ id: 'field.applicationVersion.label' })}
          value={metadata.applicationVersion}
        />

        <InfoRow label={msg({ id: 'field.description.label' })} value={metadata.description} />

        {metadata.fileSize && (
          <>
            <Divider />
            <InfoRow
              label={msg({ id: 'field.fileSize.label' })}
              value={`${(metadata.fileSize / 1024).toFixed(2)} KB`}
            />
          </>
        )}
      </Stack>

      <Divider sx={{ mt: 2, mb: 2 }} />

      <Stack direction="row" spacing={2} sx={{ marginTop: 'auto !important' }}>
        <MDButton
          type="button"
          fullWidth
          color="secondary"
          onClick={onCancel}
          disabled={isImporting}
        >
          {msg({ id: 'button.reset' })}
        </MDButton>
        <MDButton
          type="button"
          fullWidth
          color="primary"
          loading={isImporting}
          disabled={isImporting}
          onClick={onImport}
        >
          {msg({ id: 'button.import' })}
        </MDButton>
      </Stack>
    </Stack>
  );
}

ImportConfigurationMetadata.propTypes = {
  metadata: PropTypes.shape({
    uuid: PropTypes.string,
    exportDate: PropTypes.string,
    exportedBy: PropTypes.string,
    applicationVersion: PropTypes.string,
    description: PropTypes.string,
    assistantCount: PropTypes.number,
    promptCount: PropTypes.number,
    scriptCount: PropTypes.number,
    fileSize: PropTypes.number
  }).isRequired,
  importMode: PropTypes.string.isRequired,
  onImportModeChange: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onImport: PropTypes.func.isRequired,
  isImporting: PropTypes.bool,
  onExpandAll: PropTypes.func.isRequired,
  onCollapseAll: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onDeselectAll: PropTypes.func.isRequired
};

export { IMPORT_MODES };
